import { FiMic } from 'react-icons/fi';

export const hedgingSpeculatingData = {
  courseId: 'hedging-speculating',
  courseType: 'speaking-course',
  title: 'Hedging & Speculating',
  icon: FiMic,
  overview: {
    impact: 'Express uncertainty, caution, and speculation with sophistication — a hallmark of Band 7+ speakers',
    focus: 'Hedging language for softening claims, speculative structures for discussing the future, and the difference between hedging and vagueness',
    uses: 'Speaking Part 3 opinion and discussion questions, and Part 2 when speculating about feelings or outcomes',
    duration: '25 minutes',
  },

  sections: [
    {
      title: 'Understanding Hedging',
      content: `<p><strong>Hedging</strong> means softening a statement so it sounds cautious rather than absolute. In academic and professional English — and in IELTS — hedging shows intellectual maturity. Saying "Everyone agrees that..." sounds naive; saying "Most people would probably agree that..." sounds nuanced.</p>
<p>Hedging is rewarded in the IELTS band descriptors under <em>Lexical Resource</em> (showing range) and <em>Grammatical Range & Accuracy</em> (using modal verbs and complex structures correctly).</p>`,
      points: [
        'Hedging avoids overgeneralisation: "All students hate exams" → "Many students tend to find exams stressful."',
        'Modal verbs are the simplest hedging tool: might, could, may, would.',
        'Adverbs add nuance: perhaps, probably, arguably, generally, to some extent.',
        'Hedging phrases: "It seems that...", "There is some evidence that...", "I would argue that..."',
        'Hedging does NOT mean being unsure — it means being precise about how certain you are.',
      ],
      examples: [
        { label: 'Unhedged', text: 'Social media causes depression in teenagers.' },
        { label: 'Hedged', text: 'Social media may contribute to depression in some teenagers, particularly those who spend excessive time on platforms.' },
      ],
    },
    {
      title: 'Hedging Expressions',
      content: `<p>The table below ranks common hedging expressions by their <strong>certainty level</strong>, from near-certain to very tentative. Mixing expressions from different levels shows the examiner you have a wide lexical range.</p>`,
      points: [
        'Use high-certainty hedges when you are fairly confident but still want to sound measured.',
        'Use mid-certainty hedges for balanced discussion — the sweet spot for most Part 3 answers.',
        'Use low-certainty hedges when genuinely unsure or presenting a weak possibility.',
      ],
      table: {
        headers: ['Expression', 'Certainty Level', 'Example'],
        rows: [
          ['It is widely accepted that ...', '~90%', 'It is widely accepted that education improves life outcomes.'],
          ['There is strong evidence that ...', '~85%', 'There is strong evidence that exercise benefits mental health.'],
          ['It is likely that ...', '~75%', 'It is likely that remote work will continue growing.'],
          ['I would argue that ...', '~70%', 'I would argue that public transport should be subsidised.'],
          ['It seems that ... / It appears that ...', '~60%', 'It seems that younger generations are more environmentally aware.'],
          ['... tend to ...', '~60%', 'People tend to underestimate the importance of sleep.'],
          ['Perhaps / Probably', '~50%', 'Perhaps technology will eventually make traditional classrooms obsolete.'],
          ['There is some evidence that ...', '~45%', 'There is some evidence that bilingual children develop cognitive advantages.'],
          ['It could be that ... / It might be that ...', '~35%', 'It could be that people in rural areas have stronger community ties.'],
          ['To some extent, ...', '~40%', 'To some extent, I agree that competition motivates students.'],
          ['There is a slight chance that ...', '~15%', 'There is a slight chance that handwriting will disappear entirely.'],
          ['It is conceivable that ...', '~10%', 'It is conceivable that one day humans will live on Mars.'],
        ],
      },
    },
    {
      title: 'Speculating About the Future',
      content: `<p>Part 3 frequently asks you to predict or imagine future scenarios. <strong>Speculation</strong> is a specific form of hedging focused on what <em>might</em> happen. Using speculative language correctly demonstrates grammatical range.</p>`,
      points: [
        '"It\'s likely that AI will transform the job market within the next decade."',
        '"There\'s a good chance that electric cars will become the norm by 2040."',
        '"I wouldn\'t be surprised if cash disappeared entirely in some countries."',
        '"It remains to be seen whether online education will fully replace universities."',
        '"In all likelihood, climate change will force major policy shifts in the coming years."',
      ],
      examples: [
        { label: 'Confident speculation', text: 'In all likelihood, renewable energy will dominate the global market within twenty years.' },
        { label: 'Cautious speculation', text: 'It\'s hard to say for certain, but there\'s a chance that self-driving cars could become mainstream by the 2030s.' },
      ],
      warning: 'Avoid definitive predictions like "This will definitely happen." Examiners reward nuance, not certainty.',
    },
    {
      title: 'Hedging vs Vagueness',
      content: `<p>There is a crucial difference between hedging (deliberate softening) and vagueness (lack of clear ideas). Hedging adds precision; vagueness removes it. Examiners can tell the difference.</p>`,
      points: [
        'Hedging: "It seems likely that urban populations will continue to grow, partly because cities offer more employment opportunities." — clear idea, measured tone.',
        'Vagueness: "I think maybe it could be something about cities, probably." — no clear point, filler words.',
        'Hedging uses specific language (modal verbs, adverbs, hedging phrases) with a clear underlying message.',
        'Vagueness stacks multiple uncertain words without actually saying anything substantive.',
        'Test: If you remove the hedging words, does a clear statement remain? If yes, you are hedging. If no, you are being vague.',
      ],
      examples: [
        { label: 'Hedging (good)', text: 'I would argue that to some extent, globalisation has benefited developing countries by increasing trade opportunities.' },
        { label: 'Vagueness (bad)', text: 'I think maybe globalisation is sort of, you know, possibly a good thing in some way perhaps.' },
      ],
    },
  ],

  exercises: [
    {
      title: 'Choose the Hedged Version',
      type: 'select',
      instructions: 'Select the sentence that uses hedging appropriately without being vague.',
      questions: [
        {
          text: 'Which is the best hedged version of "Technology destroys jobs"?',
          options: [
            'Technology destroys jobs.',
            'Technology might potentially perhaps destroy some jobs maybe.',
            'Technology may displace certain types of jobs, particularly in manufacturing and routine tasks.',
            'Something about technology and jobs, I think.',
          ],
          answer: 'Technology may displace certain types of jobs, particularly in manufacturing and routine tasks.',
          explanation: 'This version hedges with "may" and "certain types" while remaining specific. The second option stacks hedges into vagueness; the fourth has no real content.',
        },
        {
          text: 'Which is the best hedged version of "Everyone prefers city life"?',
          options: [
            'Everyone definitely prefers living in cities.',
            'Many people tend to prefer city life, largely because of the career and social opportunities available.',
            'Cities are kind of maybe preferred by some people or something.',
            'All humans love cities.',
          ],
          answer: 'Many people tend to prefer city life, largely because of the career and social opportunities available.',
          explanation: '"Many people tend to" replaces the overgeneralisation, and "largely because" provides a specific reason — effective hedging with substance.',
        },
        {
          text: 'Which is the best hedged version of "Online learning is better than classroom learning"?',
          options: [
            'Online learning is arguably more flexible than classroom learning, although it may lack the social interaction that many students value.',
            'Online learning is absolutely better.',
            'I think probably maybe online is better, or not.',
            'Learning online is something people do.',
          ],
          answer: 'Online learning is arguably more flexible than classroom learning, although it may lack the social interaction that many students value.',
          explanation: '"Arguably" and "may lack" hedge the claim, while the concession ("although...") adds balance — a well-constructed Band 7+ sentence.',
        },
        {
          text: 'Which is the best hedged version of "Children must learn foreign languages"?',
          options: [
            'Children must learn foreign languages no matter what.',
            'There is a strong case for introducing foreign languages in primary education, as younger learners tend to acquire pronunciation more easily.',
            'Maybe children should sort of learn languages or something like that.',
            'Languages are important for children I guess.',
          ],
          answer: 'There is a strong case for introducing foreign languages in primary education, as younger learners tend to acquire pronunciation more easily.',
          explanation: '"There is a strong case for" hedges the obligation, and "tend to" softens the generalisation while keeping the argument specific and persuasive.',
        },
      ],
    },
    {
      title: 'Add Hedging to Blunt Statements',
      type: 'fill-in',
      instructions: 'Rewrite the blunt word or phrase in brackets with an appropriate hedging expression.',
      questions: [
        {
          text: 'Social media [definitely causes] anxiety in young people. → Social media ___ anxiety in young people.',
          answer: 'may contribute to',
          explanation: '"May contribute to" hedges "definitely causes" by reducing certainty (may) and softening causation (contribute to rather than cause).',
        },
        {
          text: '[Everyone knows] that exercise is good for health. → ___ exercise is beneficial for health.',
          answer: 'It is widely accepted that',
          explanation: '"It is widely accepted that" replaces the overgeneralisation "Everyone knows" with a measured, formal hedge.',
        },
        {
          text: 'Electric cars [will replace] petrol cars by 2035. → Electric cars ___ petrol cars by 2035.',
          answer: 'are likely to replace',
          explanation: '"Are likely to replace" maintains the prediction but adds appropriate uncertainty, which sounds more measured.',
        },
        {
          text: 'Working from home [always increases] productivity. → Working from home ___ productivity.',
          answer: 'can sometimes increase',
          explanation: '"Can sometimes increase" removes the absolute "always" and adds appropriate qualification.',
        },
      ],
    },
    {
      title: 'Identify Speculation Language',
      type: 'select',
      instructions: 'Choose the sentence that contains speculation about the future.',
      questions: [
        {
          text: 'Which sentence speculates about a future outcome?',
          options: [
            'Many people currently work from home.',
            'There\'s a good chance that the majority of office workers will be remote by 2030.',
            'My company allows remote work on Fridays.',
            'I prefer working from home.',
          ],
          answer: 'There\'s a good chance that the majority of office workers will be remote by 2030.',
          explanation: '"There\'s a good chance that... will be..." combines a hedged probability with a future prediction — classic speculation.',
        },
        {
          text: 'Which sentence speculates about a future outcome?',
          options: [
            'I studied environmental science at university.',
            'Pollution levels are high in many cities.',
            'The government passed new regulations last year.',
            'In all likelihood, renewable energy will become the dominant power source within two decades.',
          ],
          answer: 'In all likelihood, renewable energy will become the dominant power source within two decades.',
          explanation: '"In all likelihood" signals speculation, and "within two decades" places the prediction in the future.',
        },
        {
          text: 'Which sentence speculates about a future outcome?',
          options: [
            'Traditional shops are closing across the country.',
            'I wouldn\'t be surprised if physical bookshops disappeared entirely within a generation.',
            'Amazon is the largest online retailer.',
            'My local bookshop closed last month.',
          ],
          answer: 'I wouldn\'t be surprised if physical bookshops disappeared entirely within a generation.',
          explanation: '"I wouldn\'t be surprised if" is a speculation structure expressing a predicted future scenario with measured confidence.',
        },
        {
          text: 'Which sentence speculates about a future outcome?',
          options: [
            'University tuition is expensive in many countries.',
            'I graduated three years ago.',
            'It remains to be seen whether free university education will become a global norm.',
            'Student debt is a major issue.',
          ],
          answer: 'It remains to be seen whether free university education will become a global norm.',
          explanation: '"It remains to be seen whether" explicitly marks the statement as speculation about an uncertain future outcome.',
        },
      ],
    },
    {
      title: 'Speculate About Future Trends',
      type: 'writing',
      instructions: 'Write 3–4 sentences speculating about the given topic. Use at least two different speculation expressions.',
      questions: [
        {
          text: '"How might artificial intelligence change the workplace in the next twenty years?"',
          example: '"It\'s likely that AI will automate many repetitive tasks, which could lead to significant job losses in sectors like manufacturing and data entry. However, there\'s a good chance that new types of jobs will emerge — roles we can\'t even imagine yet. I wouldn\'t be surprised if most companies had dedicated AI management teams within the next decade. That said, it remains to be seen whether governments will adapt their education systems quickly enough to prepare workers for this shift."',
          tip: 'Vary your speculation expressions: "It\'s likely that...", "There\'s a good chance...", "I wouldn\'t be surprised if...", "It remains to be seen whether...".',
        },
        {
          text: '"Do you think people will still travel abroad for holidays in fifty years?"',
          example: '"I think people will probably still travel, but the nature of tourism may change significantly. In all likelihood, climate concerns will lead to a shift towards slower, more sustainable forms of travel, such as trains rather than flights. It\'s conceivable that virtual reality could replace some forms of tourism, allowing people to \'visit\' places from home. Having said that, I believe the human desire for real experiences means physical travel won\'t disappear entirely."',
          tip: 'Show range by mixing confident speculation ("In all likelihood") with cautious speculation ("It\'s conceivable that").',
        },
        {
          text: '"Will cash still exist in twenty years?"',
          example: '"There\'s a strong possibility that cash will become increasingly rare, especially in developed countries where digital payments are already dominant. For instance, Sweden is already close to becoming a cashless society. However, I would argue that cash is unlikely to vanish completely, because many older people and those in rural areas still depend on it. It could be that we see a two-tier system, where digital payments dominate but cash remains available as a backup."',
          tip: 'Combine a current example (Sweden) with future speculation to ground your argument in reality.',
        },
      ],
    },
    {
      title: 'Rank Certainty Levels',
      type: 'select',
      instructions: 'Choose the expression that matches the specified certainty level.',
      questions: [
        {
          text: 'Which expression signals the HIGHEST level of certainty?',
          options: ['It is conceivable that...', 'It is widely accepted that...', 'There is a slight chance that...'],
          answer: 'It is widely accepted that...',
          explanation: '"It is widely accepted that" (~90% certainty) is the strongest hedge here, implying broad consensus while still softening the claim.',
        },
        {
          text: 'Which expression signals MID-LEVEL certainty (~50-60%)?',
          options: ['It is widely accepted that...', 'It seems that...', 'It is conceivable that...'],
          answer: 'It seems that...',
          explanation: '"It seems that" (~60%) expresses a moderate impression — not confident, not dismissive.',
        },
        {
          text: 'Which expression signals the LOWEST level of certainty?',
          options: ['It is likely that...', 'I would argue that...', 'There is a slight chance that...'],
          answer: 'There is a slight chance that...',
          explanation: '"There is a slight chance that" (~15%) signals a remote possibility — the weakest certainty of the three options.',
        },
      ],
    },
  ],

  tips: [
    'Use 1–2 hedging expressions per answer — enough to show range without sounding hesitant.',
    'Match your certainty level to your actual opinion: don\'t hedge when you genuinely feel strongly.',
    'Practise converting absolute statements into hedged ones as a daily exercise.',
    'Learn the certainty ranking of expressions so you can choose the right one instinctively.',
    'Speculation and hedging work together — speculate about the future using hedged language for maximum impact.',
  ],
  dailyPractice: [
    'Take five strong claims from a news headline and rewrite each with appropriate hedging.',
    'Record yourself answering a Part 3 question and count how many hedging expressions you use — aim for two or three.',
    'Practise ranking your own opinions on a certainty scale (10%–90%) and matching them to the correct expression.',
    'Listen to a news interview and note when speakers hedge their claims — write down the exact phrases they use.',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Hedging & Speculating: This course teaches IELTS Speaking candidates how to express uncertainty and speculation with precision. Covers hedging expressions ranked by certainty level, speculative structures for future discussion, and the critical distinction between hedging and vagueness. Targets Band 7+ in Lexical Resource and Grammatical Range & Accuracy.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...hedgingSpeculatingData,
      title: hedgingSpeculatingData.title + ' (Foundations)',
      overview: { ...hedgingSpeculatingData.overview, duration: '15-20 minutes' },
      sections: hedgingSpeculatingData.sections.slice(0, Math.max(2, Math.ceil(hedgingSpeculatingData.sections.length / 2))),
      exercises: hedgingSpeculatingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...hedgingSpeculatingData,
      title: hedgingSpeculatingData.title + ' (Advanced)',
      overview: { ...hedgingSpeculatingData.overview, duration: '30-35 minutes' },
      sections: hedgingSpeculatingData.sections,
      exercises: hedgingSpeculatingData.exercises.map(ex => ({ ...ex, title: ex.title + ' (Advanced)' })),
      tips: [
        ...(hedgingSpeculatingData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return hedgingSpeculatingData;
}
